import type React from 'react';
import { useEffect, useState } from 'react';

import { alertItemWrapper } from './styles';
import { animated, useTransition } from '@react-spring/web';

import { colors, getRGBA } from '@cobliteam/base-components/src/styles';

import CancelRoundedIcon from '@material-ui/icons/CancelRounded';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';

import { AlertLeftIcon } from '../AlertLeftIcon';
import { type AlertMessage, AlertMessageContent } from '../AlertMessageContent';
import { AlertProgressBar } from '../AlertProgressBar';
import { AlertRightContent } from '../AlertRightContent';

// documentation: https://github.com/Cobliteam/herbie-dashboard/pull/4539

export enum AlertTypes {
  SUCCESS = 'ALERT_SUCCESS',
  WARNING = 'ALERT_WARNING',
  ERROR = 'ALERT_ERROR',
  INFO = 'ALERT_INFO',
}

export interface AlertConfigCTA {
  callback(): void;
  text: string;
}
export interface AlertConfig {
  hasCloseBtn?: boolean;
  ttl?: number;
  closeCallback?(): void;
  cta?: AlertConfigCTA;
}

export interface AlertItemProps {
  id: string;
  message: AlertMessage;
  messageSize?: number;
  alertType: AlertTypes;
  leftIcon?: JSX.Element;
  alertConfig?: AlertConfig;
  classes?: {
    root?: string;
    closeIcon?: string;
  };
  onCloseAlert?: (id: string) => void;
}

export const alertAssets = {
  [AlertTypes.SUCCESS]: {
    color: colors.success.default,
    leftIcon: <CheckRoundedIcon />,
    hoverColor: getRGBA(colors.success.hover, 0.3),
    progressBarColor: getRGBA(colors.success.hover, 0.7),
  },
  [AlertTypes.ERROR]: {
    color: colors.error.default,
    leftIcon: <CancelRoundedIcon />,
    hoverColor: getRGBA(colors.error.hover, 0.3),
    progressBarColor: getRGBA(colors.error.hover, 0.7),
  },
  [AlertTypes.WARNING]: {
    color: colors.warning.default,
    leftIcon: <WarningRoundedIcon />,
    hoverColor: getRGBA(colors.warning.hover, 0.3),
    progressBarColor: getRGBA(colors.warning.hover, 0.7),
  },
  [AlertTypes.INFO]: {
    color: colors.accessory._5,
    leftIcon: <InfoRoundedIcon />,
    hoverColor: getRGBA(colors.primary.default, 0.3),
    progressBarColor: getRGBA(colors.primary.default, 0.7),
  },
};

export const FIVE_SECONDS_IN_MILLIS = 5000;

const AlertItem: React.FC<AlertItemProps> = ({
  message,
  messageSize,
  alertType,
  leftIcon,
  alertConfig = {},
  classes = {},
  onCloseAlert,
  id,
}) => {
  const {
    hasCloseBtn = false,
    ttl = FIVE_SECONDS_IN_MILLIS,
    closeCallback,
    cta,
  } = alertConfig;
  const [isActive, setIsActive] = useState(true);
  const [animationStartedTime, setAnimationStartedTime] = useState<number>(0);
  const [timeoutCode, setTimeoutCode] = useState<number | undefined>(undefined);
  const [remainingTime, setRemainingTime] = useState(ttl);
  const transition = useTransition(isActive, {
    from: { x: '410px', opacity: 0 },
    enter: { x: '0px', opacity: 1 },
    leave: { x: '410px', opacity: 0 },
  });

  const handleClose = () => {
    setIsActive(false);

    window.setTimeout(() => {
      if (onCloseAlert) {
        onCloseAlert(id);
      }

      if (closeCallback) {
        closeCallback();
      }
    }, 1000);
  };

  useEffect(() => {
    if (!hasCloseBtn) {
      setAnimationStartedTime(Date.now());

      const timeout = window.setTimeout(() => {
        handleClose();
      }, ttl);

      setTimeoutCode(timeout);
    }
  }, []);

  const stopTimers = () => {
    if (animationStartedTime && timeoutCode) {
      setRemainingTime(remainingTime - (Date.now() - animationStartedTime));
      clearTimeout(timeoutCode);
    }
  };

  const resumeTimers = () => {
    if (remainingTime) {
      setAnimationStartedTime(Date.now());

      const timeout = window.setTimeout(() => {
        setIsActive(false);
      }, remainingTime);

      setTimeoutCode(timeout);
    }
  };

  return (
    <div id={id}>
      {transition((style, isVisible) =>
        isVisible ? (
          <animated.div
            className={`${alertItemWrapper(alertAssets[alertType].color)} ${
              classes.root
            }`}
            style={style}
            onMouseEnter={stopTimers}
            onMouseLeave={resumeTimers}
          >
            <AlertLeftIcon icon={leftIcon || alertAssets[alertType].leftIcon} />
            <AlertMessageContent
              cta={cta}
              message={message}
              messageSize={messageSize}
            />
            <AlertRightContent
              hoverColor={alertAssets[alertType].hoverColor}
              handleCloseAlert={handleClose}
              cta={cta}
              hasCloseBtn={hasCloseBtn}
              classes={{
                closeIcon: classes.closeIcon,
              }}
            />
            {!hasCloseBtn && (
              <AlertProgressBar
                color={alertAssets[alertType].progressBarColor}
                duration={ttl}
              />
            )}
          </animated.div>
        ) : null
      )}
    </div>
  );
};

export default AlertItem;
